<template>
  <div class="bg-gray-900 min-h-screen w-full">
    <div class="px-4 py-8 mx-auto container">
      <h1 class="text-3xl font-bold text-white mb-6">Proizvodi</h1>

      <!-- Search Component -->
      <ProductsSearch
        @filter-change="handleFilterChange"
        :initial-filters="activeFilters"
        ref="searchComponent"
      />

      <!-- Active Filters -->
      <ActiveFilters
        :filters="activeFilters"
        :filter-labels="filterLabels"
        :categories="categories"
        @remove-filter="removeFilter"
        @reset-filters="resetFilters"
      />

      <!-- Loader -->
      <div v-if="showLoader" class="flex justify-center items-center py-10">
        <div
          class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"
        ></div>
      </div>

      <!-- Error message -->
      <div v-else-if="error" class="bg-red-900 text-white p-4 rounded-lg mb-6">
        {{ error }}
      </div>

      <!-- Products list - Grid layout -->
      <div
        v-else-if="products.length > 0"
        class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 w-full max-w-6xl mx-auto"
      >
        <router-link
          v-for="product in products"
          :key="`product-${currentPage}-${product.id}`"
          :to="{
            name: 'product-detail',
            params: { id: product.slug || product.id },
          }"
          class="block w-full transition-transform duration-200 hover:-translate-y-1"
        >
          <ProductCard :product="product" />
        </router-link>
      </div>

      <!-- No products - samo ako je showNoProducts true -->
      <div
        v-else-if="showNoProducts"
        class="bg-gray-800 rounded-lg p-8 text-center text-gray-400"
      >
        Nema dostupnih proizvoda.
      </div>

      <!-- Pagination Component -->
      <PaginationComponent
        :meta="meta"
        :current-page="currentPage"
        :update-url="true"
        @page-change="changePage"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import ProductCard from '@/components/ProductCard.vue';
import ProductsSearch from '@/components/ProductsSearchComponent.vue';
import ActiveFilters from '@/components/ActiveFiltersComponent.vue';
import PaginationComponent from '@/components/PaginationComponent.vue';
import api from '@/services/api';
import categoryService from '@/services/categoryService';

interface Product {
  id: string;
  name: string;
  slug: string;
  price: string;
  mainPicture: string | null;
  followersCount: number;
  city: string;
  username: string;
  category: {
    id: string;
    name: string;
  };
}

interface Meta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface ProductsResponse {
  status: string;
  message: string;
  data: {
    data: Product[];
    meta: Meta;
  };
}

interface Category {
  id: string;
  name: string;
}

interface Filters {
  search?: string;
  categoryId?: string;
  city?: string;
  minPrice?: number;
  maxPrice?: number;
}

interface SearchComponentMethods {
  updateFilters: (filters: Filters) => void;
  resetFilters: () => void;
}

export default defineComponent({
  name: 'ProductsView',
  components: {
    ProductCard,
    ProductsSearch,
    ActiveFilters,
    PaginationComponent,
  },
  data() {
    return {
      products: [] as Product[],
      meta: null as Meta | null,
      loading: false,
      showLoader: false,
      showNoProducts: false, // Dodato za debounce "no products"
      loaderTimeout: null as number | null,
      noProductsTimeout: null as number | null, // Dodato za debounce "no products"
      error: null as string | null,
      activeFilters: {} as Filters,
      filterLabels: {
        search: 'Pretraga',
        categoryId: 'Kategorija',
        city: 'Grad',
        minPrice: 'Min. cena',
        maxPrice: 'Max. cena',
      } as const,
      categories: [] as Category[],
    };
  },
  computed: {
    currentPage(): number {
      const page = parseInt(this.$route.query.page as string, 10);
      return isNaN(page) || page < 1 ? 1 : page;
    },
    searchComponent(): SearchComponentMethods {
      return this.$refs.searchComponent as SearchComponentMethods;
    },
  },
  mounted() {
    this.fetchCategories();
  },
  beforeUnmount() {
    if (this.loaderTimeout !== null) {
      clearTimeout(this.loaderTimeout);
    }
    if (this.noProductsTimeout !== null) {
      clearTimeout(this.noProductsTimeout);
    }
  },
  watch: {
    '$route.query': {
      handler(newQuery: any, oldQuery: any) {
        console.log('Query parametri promenjeni:', newQuery);

        // Kreiraj prazne filtere
        const newFilters: Filters = {};

        // Dodaj samo filtere koji imaju vrednost
        if (newQuery.search && newQuery.search.trim() !== '') {
          newFilters.search = newQuery.search;
        }
        if (newQuery.categoryId) {
          newFilters.categoryId = newQuery.categoryId;
        }
        if (newQuery.city && newQuery.city.trim() !== '') {
          newFilters.city = newQuery.city;
        }
        if (newQuery.minPrice && !isNaN(Number(newQuery.minPrice))) {
          newFilters.minPrice = Number(newQuery.minPrice);
        }
        if (newQuery.maxPrice && !isNaN(Number(newQuery.maxPrice))) {
          newFilters.maxPrice = Number(newQuery.maxPrice);
        }

        this.activeFilters = newFilters;

        console.log('Ažurirani activeFilters:', this.activeFilters);

        // Pozovi fetchProducts
        const page = parseInt(newQuery.page as string, 10) || 1;
        this.fetchProducts(page);
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    async fetchCategories(this: any) {
      try {
        this.categories = await categoryService.getCategories();
        console.log('Categories loaded in ProductsView:', this.categories);
      } catch (err) {
        console.error('Error fetching categories:', err);
        this.categories = [];
      }
    },
    startLoader(this: any) {
      this.loading = true;

      // Sakrij "no products" poruku kada počinje loading
      this.hideNoProducts();

      if (this.loaderTimeout !== null) {
        clearTimeout(this.loaderTimeout);
      }

      this.loaderTimeout = window.setTimeout(() => {
        if (this.loading) {
          this.showLoader = true;
        }
      }, 500);
    },
    stopLoader(this: any) {
      this.loading = false;
      this.showLoader = false;

      if (this.loaderTimeout !== null) {
        clearTimeout(this.loaderTimeout);
        this.loaderTimeout = null;
      }
    },
    // Dodato za debounce "no products"
    showNoProductsDelayed(this: any) {
      // Otkaži postojeći timeout ako postoji
      if (this.noProductsTimeout !== null) {
        clearTimeout(this.noProductsTimeout);
      }

      // Postavka novi timeout za prikaz "no products"
      this.noProductsTimeout = window.setTimeout(() => {
        if (!this.loading && !this.error && this.products.length === 0) {
          this.showNoProducts = true;
        }
      }, 800); // 800ms delay
    },
    hideNoProducts(this: any) {
      this.showNoProducts = false;
      if (this.noProductsTimeout !== null) {
        clearTimeout(this.noProductsTimeout);
        this.noProductsTimeout = null;
      }
    },
    async fetchProducts(this: any, page: number = this.currentPage) {
      this.startLoader();
      this.error = null;

      try {
        const params = new URLSearchParams();
        params.append('page', page.toString());

        Object.entries(this.activeFilters).forEach(([key, value]) => {
          if (value !== null && value !== undefined && value !== '') {
            params.append(key, value.toString());
          }
        });

        const queryString = params.toString();
        const url = `/api/products/extended${
          queryString ? '?' + queryString : ''
        }`;

        const response = await api.get<ProductsResponse>(url);

        if (response.data.status === 'success') {
          this.meta = response.data.data.meta;
          this.products = response.data.data.data;

          // Ako nema proizvoda, pokaži "no products" poruku sa delay-om
          if (this.products.length === 0) {
            this.showNoProductsDelayed();
          } else {
            this.hideNoProducts();
          }
        } else {
          this.error = 'Greška prilikom učitavanja proizvoda.';
          this.hideNoProducts();
        }
      } catch (err) {
        console.error('Error fetching products:', err);
        this.error = 'Greška prilikom komunikacije sa serverom.';
        this.hideNoProducts();
      } finally {
        this.stopLoader();
      }
    },
    changePage(page: number) {
      console.log('changePage called with page:', page);
    },
    handleFilterChange(this: any, filters: Filters) {
      this.activeFilters = { ...filters };

      // Ako su filteri potpuno prazni, obriši sve query parametre osim page
      if (Object.keys(filters).length === 0) {
        this.$router.push({
          path: this.$route.path,
          query: {},
        });
        return;
      }

      // Inače, ažuriraj query parametre
      const newQuery = {
        ...Object.fromEntries(
          Object.entries(filters).filter(
            ([, value]) => value !== null && value !== undefined && value !== ''
          )
        ),
      };

      this.$router.push({
        path: this.$route.path,
        query: newQuery,
      });
    },
    removeFilter(this: any, key: keyof Filters) {
      const newFilters = { ...this.activeFilters };
      delete newFilters[key];
      this.activeFilters = newFilters;

      if (this.searchComponent) {
        this.searchComponent.updateFilters(this.activeFilters);
      }

      const newQuery = { ...this.$route.query };
      delete newQuery[key];
      delete newQuery.page;

      this.$router.push({
        path: this.$route.path,
        query: newQuery,
      });
    },
    resetFilters(this: any) {
      this.activeFilters = {};

      if (this.searchComponent) {
        this.searchComponent.resetFilters();
      }

      this.$router.push({
        path: this.$route.path,
        query: {},
      });
    },
  },
});
</script>
